import { isIntuitDomain } from '@mc/window';
import createHistory from './history';
import createRouteTrie from './matcher';
import { searchFromHref } from './url';

/**
 * A router factory. Given a set of routes, this factory will create a
 * "router". A router, internally, is comprised of two other pieces:
 *
 *   1. A history instance - to handle interfacing with the HTML5
 *      history API.
 *
 *   2. A radix trie - a data structure for holding a static routing
 *      configuration, and an API to match against it.
 *
 * All together, the router handles the interop of these two components,
 * providing a higher-level API.
 *
 * The router handles event emitting, exposing an "entry" object for
 * every navigation. An entry consists of two top-level properties:
 *
 *   1. route - the configuration of the matched route. This includes
 *      parameters inferred from the URL.
 *
 *   2. location - the current location object from the history instance.
 *
 * The router also provides some APIs for eagerly fetching dependencies
 * of routes (e.g. components which may be "code-split"/asynchronously
 * fetched, in addition to page-data/the data dependencies for a route).
 */
export default function createRouter({
  redirect = () => {},
  referrer,
  routes,

  // Routes that will trigger a browser refresh upon navigation to them.
  // See XP-846 for more
  forceRefreshRoutes = [],
}: $TSFixMe) {
  // Create a radix trie for matching routes against.
  const matcher = createRouteTrie(routes);
  const refreshMatcher = createRouteTrie(forceRefreshRoutes);

  // Create a history instance with a custom `isPathNavigable`. This
  // option helps the history instance know which navigation API to
  // use (location.assign vs. history.pushState/history.replaceState).
  // That is to say, when a path does not exist in the route tree, we
  // want to use the browser's default navigation API.
  const history = createHistory({
    referrer,
    // @ts-expect-error TS(2322): Type '(path: any) => boolean' is not assignable to... Remove this comment to see the full error message
    isPathNavigable: function (path: string) {
      if (refreshMatcher.match(path) !== undefined) {
        return false;
      }
      return matcher.match(path) !== undefined;
    },
  });

  // We locally wrap the subscription API from the history instance,
  // in order to provide a slightly higher-level API (we emit an "entry",
  // which consists of both a route configuration and location object).
  // In order to do this, we need to manage our own set of subscribers.
  const subscriptions = new Set();

  history.subscribe((location: $TSFixMe) => {
    // Because we use a custom isPathNavigable, there should never
    // be a no-match here. We can account for this state should we
    // discover some ways to force it in-app.
    const route = matcher.match(location.pathname);
    // See if we need to redirect before loading dependencies or
    // emitting events upstream. When we do, we use a replaceState.
    const redirectTo = redirect(route, location);
    if (redirectTo !== undefined) {
      history.redirect(redirectTo.url, redirectTo.message);
      return;
    }

    // Check if code is loading in AppFabric app like Fusion
    if (!isIntuitDomain()) {
      // Immediately kick off any async work that needs to happen.
      // More specifically, start downloading the potentially code-split
      // component, as well as the page data associated with the route.
      loadRouteDependencies(route, location.href);
    } else {
      // This check is to make sure there is a route defined when in AppFabric app, otherwise
      // there could be errors where the route is undefined and `loadRouteDependencies`
      // throws an error.
      if (route) {
        loadRouteDependencies(route, location.href);
      }
    }

    // Emit event
    subscriptions.forEach((callback) => {
      (callback as $TSFixMe)({
        location,
        route,
      });
    });
  });

  // Ensure we have an initial route up front. If not, traverse upward
  // til we have a match. Once we have one, preload any initial route
  // dependencies before kicking off other work.
  let initialLocation = history.location;
  let initialRoute = matcher.match(initialLocation.pathname);
  const hasInitialRoute = initialRoute !== undefined;

  // Edge case: if the flagging on the server is inconsistent with
  // the route flagging on the client, the user might hit this code
  // path. If we don't do anything then the entire app will break.
  if (!hasInitialRoute) {
    if (__DEV__) {
      // This should never really happen, but if it does, we should
      // make the error as clear as possible.
      console.error(
        `${history.location.pathname} was not found as a route in the ` +
          'single-page app. Did you define the route and add it to the ' +
          'routes array?',
      );
    }

    // Find the nearest match and replace state to it.
    const nearest = matcher.nearest(initialLocation.pathname);
    history.redirect(nearest?.path);

    initialLocation = history.location;
    initialRoute = nearest;
  }

  // Ensure that we handle any potential redirects before booting up the
  // single-page app.
  const redirectTo = redirect(initialRoute, initialLocation);

  if (redirectTo !== undefined) {
    history.redirect(redirectTo.url, redirectTo.message);
  } else {
    loadRouteDependencies(initialRoute, initialLocation.href);
  }

  // A helper function to prefetch component code and page data.
  // The contract is quite loose, but so long as the data dependency
  // provides a `load` method, we will call it. Same for the value
  // of `component`.
  function loadRouteDependencies(route: $TSFixMe, href: string) {
    if (route.data && route.data.load) {
      const search = searchFromHref(href);
      route.data.load(search);
    }

    if (route.component.load) {
      route.component.load();
    }
  }

  // Similar to the helper above, though in this instance, we only
  // load the component code, and no data. Used when intent to navigate
  // stems from a weaker signal (e.g. hovering on a link element).
  function loadRouteComponentDependency(route: $TSFixMe) {
    if (route.component.load) {
      route.component.load();
    }
  }

  // The API of the router
  return {
    /**
     * Returns the current "entry" of the router. This object is comprised
     * of the location and route objects.
     */
    get currentEntry() {
      const location = history.location;
      const route = matcher.match(location.pathname);

      if (route !== undefined) {
        return {
          location,
          route,
        };
      }

      return undefined;
    },

    /**
     * API to subscribe to route changes. Takes a callback, invokes it
     * every route change with the latest "entry".
     *
     * @param {Function} callback The event emitter callback
     * @return {Function} An disposal handle, for event cleanup
     */
    subscribe: function subscribe(callback: $TSFixMe) {
      subscriptions.add(callback);

      return function unsubscribe() {
        subscriptions.delete(callback);
      };
    },

    /**
     * A method to inspect whether or not a route exists in the route tree.
     *
     * @param {String} path The pathname of a potential route.
     */
    hasPath: function hasPath(path: string) {
      return matcher.match(path) !== undefined;
    },

    /**
     * Returns the previous address of the application. When no such value
     * exists, instead of returning an empty value, callers will receive a
     * root path (i.e., `/`).
     *
     * @return {String} The previous page the browser was on.
     */
    getReferrer: history.getReferrer,

    /**
     * The API to navigate programmatically.
     *
     * @param {String} href The destination of a navigation.
     */
    navigate: history.navigate,

    /**
     * The API to programmatically "redirect" (i.e. replaceState).
     *
     * @param {String} to The destination of a navigation.
     * @param {String?} message The redirect's annotating message
     */
    redirect: history.redirect,

    /**
     * Retrieve the messaging for redirecting, should one exist.
     */
    getRedirectMessage: history.getRedirectMessage,

    /**
     * Sets a custom back button destination and returns a thunk function
     * to restore the default `goBack` behaviour.
     *
     * @param {String} path
     */
    overrideBackDestination: history.overrideBackDestination,

    /**
     * Goes back one entry in the stack. If a custom back destination has been
     * set, navigates to that path instead.
     */
    goBack: history.goBack,

    /**
     * The API to register a confirmation dialog.
     *
     * @param {Function} fn Function which returns a string to confirm against.
     * @return {Function} A cleanup function
     */
    addConfirmation: history.addConfirmation,

    /**
     * The API to load all async dependencies of a given href.
     *
     * @param {String} href The address of a potential route.
     */
    preload: function (href: string) {
      const route = matcher.match(href);

      if (route !== undefined) {
        loadRouteDependencies(route, href);
      }
    },

    /**
     * The API to load the code dependency of a given href.
     *
     * @param {String} href The address of a potential route.
     */
    preloadComponent: function (href: string) {
      const route = matcher.match(href);

      if (route !== undefined) {
        loadRouteComponentDependency(matcher.match(href));
      }
    },
  };
}
